import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle, XCircle, Clock, House, WarningCircle } from "@phosphor-icons/react";
import PublicAppointmentCalendar from "@/components/PublicAppointmentCalendar";

/**
 * Iter172 — Pagina pubblica di prenotazione appuntamento.
 * Rotta: `/book-appointment/:token`
 * Non richiede login. Mostra dettagli, slot proposti e raccoglie
 * Nome/Cognome + selezione minima (2 su 3, 3 su 4, ecc.) oppure
 * dichiarazione "Non disponibile".
 */
export default function PublicAppointmentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [unavailable, setUnavailable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${backendUrl}/api/appointments/public/${token}`);
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          setLoadError(j.detail || "Link non valido o scaduto");
          return;
        }
        const j = await r.json();
        setData(j);
      } catch (e) {
        setLoadError("Errore di rete");
      }
    })();
  }, [token, backendUrl]);

  const toggleSlot = (id) => {
    if (unavailable) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const minReq = data?.min_required_selections || 2;
  const enoughSelected = unavailable || selected.size >= minReq;
  const canSubmit = firstName.trim() && lastName.trim() && enoughSelected && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${backendUrl}/api/appointments/public/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          selected_slot_ids: Array.from(selected),
          unavailable,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Errore invio");
      }
      const j = await r.json();
      setDone(j);
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="max-w-md mx-auto mt-20 px-4">
        <div className="bg-white border rounded-lg p-6 text-center shadow-sm" data-testid="public-appointment-error">
          <WarningCircle size={40} weight="bold" className="mx-auto text-red-500 mb-2" />
          <h1 className="font-display text-xl font-bold text-slate-900 mb-1">Link non valido</h1>
          <p className="text-sm text-slate-600">{loadError}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="text-center mt-20 text-slate-500">Caricamento…</div>;

  if (done) {
    return (
      <div className="max-w-md mx-auto mt-20 px-4">
        <div className="bg-white border rounded-lg p-6 text-center shadow-sm" data-testid="public-appointment-done">
          <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500 mb-2" />
          <h1 className="font-display text-xl font-bold text-slate-900 mb-1">Grazie!</h1>
          <p className="text-sm text-slate-600">
            {done.status === "confirmed"
              ? "Appuntamento confermato. Il professionista sarà avvisato via email con la data comune."
              : "La tua risposta è stata registrata. Riceverai un aggiornamento quando tutti avranno risposto."}
          </p>
        </div>
      </div>
    );
  }

  const alreadyClosed = data.status === "confirmed" || data.status === "unavailable";

  return (
    <div className="max-w-lg mx-auto py-10 px-4">
      <div className="bg-white border rounded-lg shadow-sm overflow-hidden" data-testid="public-appointment-page">
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white px-5 py-4">
          <div className="flex items-center gap-2 text-xs opacity-90">
            <CalendarCheck size={14} weight="bold" /> Prenotazione Appuntamento
          </div>
          <h1 className="font-display text-2xl font-bold mt-1" data-testid="public-appointment-title">{data.title}</h1>
          {/* Iter182 — Istruzioni immediate ben visibili, subito sotto il titolo. */}
          <div
            className="mt-3 rounded-md bg-white/20 backdrop-blur-sm ring-1 ring-white/40 px-3 py-2 text-sm font-bold text-white leading-snug"
            data-testid="public-appointment-instructions"
          >
            Seleziona almeno due o più date tra quelle proposte e clicca su Invia per confermare
          </div>
          {/* Iter185 — Luogo appuntamento (studio del pro, immobile o indirizzo/URL custom) */}
          {(data.location_display || data.property) && (
            <div className="text-xs opacity-90 mt-1 flex items-start gap-1" data-testid="public-appointment-location">
              <House size={12} weight="bold" className="mt-0.5 shrink-0" />
              <span className="break-words">
                <span className="font-semibold">Luogo:&nbsp;</span>
                {data.location_display && /^https?:\/\//i.test(data.location_display) ? (
                  <a
                    href={data.location_display}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-white"
                  >
                    {data.location_display}
                  </a>
                ) : (
                  data.location_display || [data.property?.address, data.property?.city].filter(Boolean).join(", ")
                )}
              </span>
            </div>
          )}
        </div>
        <div className="p-5 space-y-4">
          {data.description && (
            <p className="text-sm text-slate-700 whitespace-pre-line">{data.description}</p>
          )}
          {alreadyClosed && (
            <div className="text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800">
              Questa richiesta è già stata chiusa.
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nome *</label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={alreadyClosed}
                data-testid="public-first-name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Cognome *</label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={alreadyClosed}
                data-testid="public-last-name"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Email <span className="text-slate-400 font-normal">(per ricevere conferma e file calendario)</span>
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={alreadyClosed}
              placeholder="tuo@indirizzo.it"
              data-testid="public-email"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-slate-600">
                Le tue disponibilità
              </label>
              <span className="text-[11px] text-slate-500" data-testid="public-selection-count">{selected.size}/{data.slots?.length || 0}</span>
            </div>
            {/* Iter208.16 — Calendario stile Google Calendar in sostituzione della lista */}
            <PublicAppointmentCalendar
              slots={data.slots || []}
              selected={selected}
              onToggle={(sid) => { if (!unavailable && !alreadyClosed) toggleSlot(sid); }}
              disabled={unavailable || alreadyClosed}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={unavailable}
              onChange={(e) => { setUnavailable(e.target.checked); if (e.target.checked) setSelected(new Set()); }}
              disabled={alreadyClosed}
              data-testid="public-unavailable"
            />
            <XCircle size={14} className="text-red-500" /> Non sono disponibile in nessuno dei giorni proposti
          </label>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit || alreadyClosed}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="public-submit"
          >
            {submitting ? "Invio…" : "Invia disponibilità"}
          </Button>
        </div>
      </div>
    </div>
  );
}
